import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Alert,
  Box,
  Button,
  ColumnLayout
} from '@cloudscape-design/components';

function SummaryGroup() {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load summary groups when component mounts
    loadSummaryGroups();
  }, []);

  const loadSummaryGroups = async () => {
    setLoading(true);
    try {
      // TODO: Load summary groups from DynamoDB
      console.log('Loading summary groups...');
    } catch (error) {
      console.error('Error loading summary groups:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Group related performance reports for validation and ranking"
          actions={
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={() => console.log('Create new summary group')}
            >
              Create Summary Group
            </Button>
          }
        >
          Summary Groups
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert type="info" header="Summary Group Feature">
          <Box variant="p">
            Summary groups allow you to organize and validate multiple performance reports together 
            to ensure consistency, enforce force distribution quotas, and establish relative rankings.
          </Box>
        </Alert>

        <Container
          header={
            <Header variant="h3">
              What are Summary Groups?
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Box variant="p">
              A Summary Group is a collection of related performance reports that must be validated 
              together for:
            </Box>
            
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="h4">Consistency Checks</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Grade/Rate (Block 2)</Box>
                  <Box>• Designator (Block 3)</Box>
                  <Box>• Duty/Competitive Status (Block 5)</Box>
                  <Box>• Promotion Status (Block 8)</Box>
                  <Box>• Occasion for Report (Block 10-13)</Box>
                  <Box>• Report End Date (Block 15)</Box>
                </SpaceBetween>
              </div>
              
              <div>
                <Box variant="h4">Force Distribution</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Promotion quotas enforcement</Box>
                  <Box>• Distribution percentages</Box>
                  <Box>• Must Promote limits</Box>
                  <Box>• Promotable allocations</Box>
                  <Box>• Progressing minimums</Box>
                  <Box>• Significant Problems caps</Box>
                </SpaceBetween>
              </div>

              <div>
                <Box variant="h4">Relative Rankings</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Rank order within group</Box>
                  <Box>• Comparative performance</Box>
                  <Box>• Promotion recommendations</Box>
                  <Box>• Summary letter generation</Box>
                  <Box>• Board submission order</Box>
                  <Box>• Competitive standing</Box>
                </SpaceBetween>
              </div>
            </ColumnLayout>

            <Alert type="warning" header="Coming Soon">
              <Box variant="p">
                Summary Group creation and validation functionality is currently under development. 
                This feature will allow you to:
              </Box>
              <Box padding={{ top: 's', left: 'm' }}>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Create new summary groups</Box>
                  <Box>• Add multiple performance reports to a group</Box>
                  <Box>• Validate consistency across all reports</Box>
                  <Box>• Enforce force distribution quotas</Box>
                  <Box>• Establish relative rankings</Box>
                  <Box>• Generate summary letters (NAVPERS 1610/1)</Box>
                  <Box>• Track group submission status</Box>
                </SpaceBetween>
              </Box>
            </Alert>
          </SpaceBetween>
        </Container>

        <Container
          header={
            <Header variant="h3">
              How It Works
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <div>
              <Box variant="h4" padding={{ bottom: 'xs' }}>Step 1: Create Summary Group</Box>
              <Box variant="p">
                Select multiple performance reports that belong together (e.g., all reports for a 
                specific reporting period, promotion board, or competitive category).
              </Box>
            </div>

            <div>
              <Box variant="h4" padding={{ bottom: 'xs' }}>Step 2: Consistency Validation</Box>
              <Box variant="p">
                The system automatically validates that required fields match across all reports 
                in the group, flagging any inconsistencies that would invalidate the summary.
              </Box>
            </div>

            <div>
              <Box variant="h4" padding={{ bottom: 'xs' }}>Step 3: Force Distribution Check</Box>
              <Box variant="p">
                Verify that promotion recommendations meet Navy force distribution requirements. 
                The system ensures quotas are not exceeded and minimum distributions are met.
              </Box>
            </div>

            <div>
              <Box variant="h4" padding={{ bottom: 'xs' }}>Step 4: Relative Ranking</Box>
              <Box variant="p">
                Establish rank order within the group based on performance marks and recommendations. 
                This determines the order for promotion board consideration.
              </Box>
            </div>

            <div>
              <Box variant="h4" padding={{ bottom: 'xs' }}>Step 5: Generate Summary</Box>
              <Box variant="p">
                Once validated and ranked, generate the NAVPERS 1610/1 Summary Letter automatically 
                with all required information, rankings, and distribution data from the group.
              </Box>
            </div>
          </SpaceBetween>
        </Container>

        <Container
          header={
            <Header variant="h3">
              Force Distribution Requirements
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Box variant="p">
              Navy force distribution policy requires specific percentages for promotion recommendations 
              within each summary group:
            </Box>

            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="h4">Officer Distribution</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Must Promote (MP): Up to 50%</Box>
                  <Box>• Promotable (P): Up to 40%</Box>
                  <Box>• Progressing (PR): Minimum 10%</Box>
                  <Box>• Significant Problems (SP): As needed</Box>
                </SpaceBetween>
              </div>

              <div>
                <Box variant="h4">Validation Rules</Box>
                <SpaceBetween direction="vertical" size="xs">
                  <Box>• Total must equal 100%</Box>
                  <Box>• MP + P cannot exceed 90%</Box>
                  <Box>• PR must be at least 10%</Box>
                  <Box>• SP used only when warranted</Box>
                </SpaceBetween>
              </div>
            </ColumnLayout>

            <Alert type="info">
              <Box variant="p">
                The system will automatically calculate and validate force distribution percentages 
                as you add reports to the summary group, alerting you to any violations before submission.
              </Box>
            </Alert>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Container>
  );
}

export default SummaryGroup;
